import { useState, useEffect, useMemo } from "react";
import {
  IconPilcrow,
  IconTypography,
  IconCalculator,
  IconPhoto,
  IconCode,
  IconListNumbers,
  IconQuote,
  IconTable,
  IconNote,
  IconFile,
  IconBook,
  IconList,
  IconMinus,
  IconColumns,
  IconSuperscript,
  IconTerminal2,
  IconBinaryTree,
  IconInfoCircle,
  IconPencil,
  IconLayoutSidebar,
  IconBookmark,
  IconFileText,
  IconMessageCircle,
  IconPhotoHeart,
  IconSeparator,
  IconIdBadge2,
  IconLayoutList,
} from "@tabler/icons-react";
import type { Icon } from "@tabler/icons-react";
import { api, type BlockTypeMetadata } from "@/lib/api";
import { useTranslation } from "@/utils/hooks/useTranslation";
import type { BlockType } from "./BlockTypeMenu";

export interface BlockTypeInfo {
  type: BlockType;
  label: string;
  description: string;
  icon: Icon;
  defaultContent: Record<string, unknown>;
}

// Map API iconName strings to Tabler icon components
const iconMap: Record<string, Icon> = {
  paragraphMark: IconPilcrow,
  fontFamily: IconTypography,
  calculator: IconCalculator,
  image: IconPhoto,
  code: IconCode,
  listOrdered: IconListNumbers,
  rightDoubleQuotes: IconQuote,
  table: IconTable,
  stickyNote: IconNote,
  file: IconFile,
  book: IconBook,
  listUnordered: IconList,
  horizontalRule: IconMinus,
  layoutSideBySide: IconColumns,
  layoutSideByLarge: IconColumns,
  superscript: IconSuperscript,
  terminal2: IconTerminal2,
  binaryTree: IconBinaryTree,
  cpu: IconBinaryTree, // API sends "cpu" for the algorithm block; reuse the tree glyph

  infoCircle: IconInfoCircle,
  pencil: IconPencil,
  layoutSidebar: IconLayoutSidebar,
  bookmark: IconBookmark,
  fileText: IconFileText,
  messageCircle: IconMessageCircle,
  photoHeart: IconPhotoHeart,
  separator: IconSeparator,
  // CV / résumé blocks — match the iconName values registered in
  // BlockTypeService.cs for PersonalInfo, Photo, CvSection, CvEntry.
  idBadge2: IconIdBadge2,
  photo: IconPhoto,
  layoutList: IconLayoutList,
  list: IconList,
};

// Static fallback structure (without translated strings)
const fallbackBlockTypesDefs: Omit<BlockTypeInfo, "label" | "description">[] = [
  { type: "paragraph", icon: IconPilcrow, defaultContent: { text: "" } },
  { type: "heading", icon: IconTypography, defaultContent: { text: "", level: 1 } },
  { type: "equation", icon: IconCalculator, defaultContent: { latex: "", displayMode: true } },
  { type: "figure", icon: IconPhoto, defaultContent: { src: "", caption: "", alt: "" } },
  { type: "code", icon: IconCode, defaultContent: { code: "", language: "javascript" } },
  { type: "list", icon: IconListNumbers, defaultContent: { items: [], ordered: false } },
  { type: "blockquote", icon: IconQuote, defaultContent: { text: "" } },
  { type: "table", icon: IconTable, defaultContent: { headers: ["Column 1", "Column 2", "Column 3"], rows: [["", "", ""], ["", "", ""]] } },
  { type: "theorem", icon: IconNote, defaultContent: { theoremType: "theorem", title: "", text: "", label: "" } },
  { type: "abstract", icon: IconFile, defaultContent: { title: "Abstract", text: "" } },
  { type: "title", icon: IconFileText, defaultContent: { title: "", author: "", date: "\\today", dateMode: "today" } },
  { type: "bibliography", icon: IconBook, defaultContent: { title: "References", style: "apa", entries: [] } },
  { type: "tableOfContents", icon: IconList, defaultContent: { title: "Table of Contents" } },
  { type: "pageBreak", icon: IconMinus, defaultContent: {} },
  { type: "columnBreak", icon: IconColumns, defaultContent: {} },
  { type: "footnote", icon: IconSuperscript, defaultContent: { text: "" } },
  { type: "embed", icon: IconTerminal2, defaultContent: { engine: "latex", code: "", label: "", caption: "" } },
  { type: "algorithm", icon: IconBinaryTree, defaultContent: { caption: "", label: "", lines: [{ indent: 0, keyword: "", text: "", comment: "" }] } },
  { type: "callout", icon: IconInfoCircle, defaultContent: { calloutType: "note", title: "", text: "", collapsible: false } },
  { type: "verse", icon: IconPencil, defaultContent: { lines: [], attribution: "", title: "" } },
  { type: "aside", icon: IconLayoutSidebar, defaultContent: { text: "", title: "", asideType: "sidebar" } },
  { type: "frontMatter", icon: IconBookmark, defaultContent: { text: "", subType: "titlePage" } },
  { type: "backMatter", icon: IconFileText, defaultContent: { text: "", subType: "glossary", entries: [] } },
  { type: "annotation", icon: IconMessageCircle, defaultContent: { text: "", originalContent: "", annotationType: "comment", status: "pending" } },
  { type: "cover", icon: IconPhotoHeart, defaultContent: { src: "", title: "", subtitle: "", author: "" } },
  { type: "chapterBreak", icon: IconSeparator, defaultContent: { label: "" } },
];

// Translation key mapping: type -> { labelKey, descKey }
const blockTypeTranslationKeys: Record<string, { labelKey: string; descKey: string }> = {
  paragraph: { labelKey: "paragraph", descKey: "paragraphDesc" },
  heading: { labelKey: "heading", descKey: "headingDesc" },
  equation: { labelKey: "equation", descKey: "equationDesc" },
  figure: { labelKey: "figure", descKey: "figureDesc" },
  code: { labelKey: "code", descKey: "codeDesc" },
  list: { labelKey: "list", descKey: "listDesc" },
  blockquote: { labelKey: "blockquote", descKey: "blockquoteDesc" },
  table: { labelKey: "table", descKey: "tableDesc" },
  theorem: { labelKey: "theorem", descKey: "theoremDesc" },
  abstract: { labelKey: "abstract", descKey: "abstractDesc" },
  title: { labelKey: "title", descKey: "titleDesc" },
  bibliography: { labelKey: "bibliography", descKey: "bibliographyDesc" },
  tableOfContents: { labelKey: "tableOfContents", descKey: "tableOfContentsDesc" },
  pageBreak: { labelKey: "pageBreak", descKey: "pageBreakDesc" },
  columnBreak: { labelKey: "columnBreak", descKey: "columnBreakDesc" },
  footnote: { labelKey: "footnote", descKey: "footnoteDesc" },
  embed: { labelKey: "embed", descKey: "embedDesc" },
  algorithm: { labelKey: "algorithm", descKey: "algorithmDesc" },
  callout: { labelKey: "callout", descKey: "calloutDesc" },
  verse: { labelKey: "verse", descKey: "verseDesc" },
  aside: { labelKey: "aside", descKey: "asideDesc" },
  frontMatter: { labelKey: "frontMatter", descKey: "frontMatterDesc" },
  backMatter: { labelKey: "backMatter", descKey: "backMatterDesc" },
  annotation: { labelKey: "annotation", descKey: "annotationDesc" },
  cover: { labelKey: "cover", descKey: "coverDesc" },
  chapterBreak: { labelKey: "chapterBreak", descKey: "chapterBreakDesc" },
};

function mapApiToBlockTypeInfo(meta: BlockTypeMetadata): BlockTypeInfo {
  return {
    type: meta.type as BlockType,
    label: meta.label,
    description: meta.description,
    icon: iconMap[meta.iconName] ?? IconPilcrow,
    defaultContent: meta.defaultContent,
  };
}

// Module-level cache so the API is only called once across all component instances
let cachedApiBlockTypes: BlockTypeInfo[] | null = null;
let fetchPromise: Promise<BlockTypeInfo[]> | null = null;

/**
 * Block types withdrawn from the insert surfaces.
 *
 * Scope is articles, reports and books — 85.6% of the 2026 corpus from two
 * document classes. Two families are out:
 *
 * - **CV** (`personalInfo`, `photo`, `cvSection`, `cvEntry`) — 2.2% → 1.7%.
 * - **Slides** (`slide`) — 12.1% → 8.8%, falling 27%. It is the only document
 *   category that is actually *declining*, which is a stronger argument against
 *   investing in it than its size is.
 *
 * These are hidden rather than removed: the block types still exist on the
 * server and their node views still render (`SlideBlock`, the CV blocks in
 * `BlockWorkbench`), so any document that already contains them keeps working —
 * and re-enabling is deleting a string from this list.
 *
 * Deactivating them here rather than server-side keeps the API's catalogue
 * honest about what it can still render, which imports depend on.
 */
export const DEACTIVATED_BLOCK_TYPES = new Set([
	// CV family
	"personalInfo",
	"photo",
	"cvSection",
	"cvEntry",
	// Presentation family
	"slide",
]);

function fetchBlockTypes(): Promise<BlockTypeInfo[]> {
  if (cachedApiBlockTypes) return Promise.resolve(cachedApiBlockTypes);
  if (fetchPromise) return fetchPromise;

  fetchPromise = api.blockTypes
    .list(undefined, "document")
    .then(({ blockTypes }) => {
      cachedApiBlockTypes = blockTypes
        .filter((bt) => !DEACTIVATED_BLOCK_TYPES.has(bt.type))
        .map(mapApiToBlockTypeInfo);
      return cachedApiBlockTypes;
    })
    .catch(() => {
      // Return null to signal that we should use translated fallbacks
      return null as unknown as BlockTypeInfo[];
    });

  return fetchPromise;
}

export function useBlockTypes(): BlockTypeInfo[] {
  const { t } = useTranslation('blocks');

  // Build translated fallback list
  const translatedFallbacks = useMemo(() => {
    return fallbackBlockTypesDefs.map((def) => {
      const keys = blockTypeTranslationKeys[def.type];
      return {
        ...def,
        label: keys ? t(keys.labelKey) : def.type,
        description: keys ? t(keys.descKey) : "",
      };
    });
  }, [t]);

  const [apiBlockTypes, setApiBlockTypes] = useState<BlockTypeInfo[] | null>(cachedApiBlockTypes);

  useEffect(() => {
    if (cachedApiBlockTypes) {
      setApiBlockTypes(cachedApiBlockTypes);
      return;
    }

    fetchBlockTypes().then((result) => {
      if (result) {
        setApiBlockTypes(result);
      }
    });
  }, []);

  // If API returned data, use it; otherwise use translated fallbacks
  return apiBlockTypes ?? translatedFallbacks;
}

export function getDefaultContent(type: BlockType): Record<string, unknown> {
  if (cachedApiBlockTypes) {
    const bt = cachedApiBlockTypes.find((b) => b.type === type);
    if (bt) return { ...bt.defaultContent };
  }
  const def = fallbackBlockTypesDefs.find((b) => b.type === type);
  return def ? { ...def.defaultContent } : {};
}
